import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import { useProfile } from '../context/ProfileContext';
import { scanJobs } from '../openaiClient';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';

const { FiTrendingUp, FiCheckCircle, FiClock, FiBriefcase, FiX, FiExternalLink } = FiIcons;

const StatCard = ({ title, value, trend, icon, color }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
        <h3 className="text-3xl font-bold text-gray-800">{value}</h3>
      </div>
      <div className={`p-3 rounded-xl ${color}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
    </div>
    <div className="mt-4 flex items-center text-sm">
      <SafeIcon icon={FiTrendingUp} className="text-green-500 mr-1" />
      <span className="text-green-500 font-medium">{trend}</span>
      <span className="text-gray-400 ml-2">vs last month</span>
    </div>
  </motion.div>
);

const Dashboard = () => {
  const { session } = useAuth();
  const { data, loading, refreshData } = useProfile();
  const [isScanning, setIsScanning] = useState(false);
  const [scannedJobs, setScannedJobs] = useState([]);
  const [showScannedJobsModal, setShowScannedJobsModal] = useState(false);
  
  if (loading || !data) {
    return (
      <div className="p-8 max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
        <div className="text-xl text-gray-500 font-medium animate-pulse">Loading Command Center...</div>
      </div>
    );
  }

  const handleApply = async (job) => {
    try {
      await supabase
        .from('applications')
        .insert({
          profile_id: session.user.id,
          company: job.company,
          role: job.role,
          status: 'Applied',
          match_score: job.matchScore
        });

      if (job.url) {
        window.open(job.url, '_blank');
      }

      refreshData();

      const newJobs = scannedJobs.filter(j => j !== job);
      setScannedJobs(newJobs);
      if (newJobs.length === 0) {
        setShowScannedJobsModal(false);
      }
    } catch (error) {
      console.error("Error tracking application:", error);
    }
  };

  const handleScanJob = async () => {
    if (!data.user.title) {
      alert("Please set a Job Title in your profile settings to scan for matched jobs.");
      return;
    }

    setIsScanning(true);
    try {
      const response = await scanJobs(data.user.title);
      // The scanJobs function already parses the response to JSON via response.json()
      // But OpenAI returns the JSON as a string in the "body" of the function response.
      // Wait, netlify function returns the OpenAI content which is a JSON string. Let me double check what scanJobs returns.
      // openaiClient.js: "const data = await response.json(); return data;"
      // Netlify function: body: aiData.choices[0].message.content (this is a stringified JSON)
      // So response itself IS a string, not an object. Let's handle both cases gracefully.
      const parsedData = typeof response === 'string' ? JSON.parse(response) : response;
      const { jobs } = parsedData;
      if (jobs && jobs.length > 0) {
        setScannedJobs(jobs);
        setShowScannedJobsModal(true);
      } else {
        alert("No suitable jobs found.");
      }
    } catch (error) {
      console.error("Error scanning jobs:", error);
      alert("Failed to scan jobs.");
    } finally {
      setIsScanning(false);
    }
  };

  const chartOption = {
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], axisLine: { show: false } },
    yAxis: { type: 'value', splitLine: { lineStyle: { type: 'dashed', color: '#f3f4f6' } } },
    series: [{
      data: [82, 93, 90, 95, 96, 91, 98],
      type: 'line',
      smooth: true,
      lineStyle: { width: 4, color: '#3b82f6' },
      areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(59,130,246,0.2)' }, { offset: 1, color: 'rgba(59,130,246,0)' }] } }
    }],
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight italic uppercase">Command Center</h1>
        <p className="text-gray-500 mt-1">Real-time market analysis for <span className="text-blue-600 font-bold">{data.user.title}</span>.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Avg. Match" value={data.stats.match} trend="+5.4%" icon={FiCheckCircle} color="bg-green-100 text-green-600" />
        <StatCard title="Active Apps" value={data.stats.active} trend="+2" icon={FiBriefcase} color="bg-blue-100 text-blue-600" />
        <StatCard title="Interviews" value={data.stats.interviews} trend="+1" icon={FiClock} color="bg-purple-100 text-purple-600" />
        <StatCard title="Market Rank" value={data.stats.rank} trend="+1.2%" icon={FiTrendingUp} color="bg-orange-100 text-orange-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
            <h3 className="text-lg font-bold text-gray-800 mb-6 font-black uppercase tracking-tight">Optimization Curve</h3>
            <ReactECharts option={chartOption} style={{ height: '320px' }} />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm h-fit">
          <h3 className="text-lg font-bold text-gray-800 mb-6 font-black uppercase tracking-tight">Active Funnel</h3>
          <div className="space-y-6">
            {data.applications.map((app) => (
              <div key={app.id} className="group relative">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center font-bold text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                    {app.company.charAt(0)}
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <h4 className="text-sm font-bold text-gray-900 truncate uppercase">{app.role}</h4>
                    <p className="text-xs text-gray-500 truncate">{app.company}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-sm font-black text-gray-900">{app.matchScore}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button disabled={isScanning} onClick={handleScanJob} className="w-full mt-8 py-3 bg-gray-900 text-white rounded-xl font-bold text-xs hover:bg-blue-600 transition-all uppercase tracking-widest shadow-lg shadow-gray-900/10 disabled:bg-gray-400 disabled:cursor-not-allowed">
            {isScanning ? 'Scanning...' : 'Scan New Jobs'}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showScannedJobsModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowScannedJobsModal(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
              animate={{ opacity: 1, scale: 1, y: "-50%", x: "-50%" }}
              exit={{ opacity: 0, scale: 0.95, y: "-50%", x: "-50%" }}
              className="fixed left-1/2 top-1/2 w-full max-w-2xl bg-white rounded-3xl shadow-2xl z-[70] p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-gray-900 uppercase">Scouted Opportunities</h3>
                <button onClick={() => setShowScannedJobsModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} className="text-gray-400" />
                </button>
              </div>

              <div className="space-y-4">
                {scannedJobs.map((job, idx) => (
                  <div key={idx} className="bg-gray-50 border border-gray-100 p-5 rounded-2xl flex flex-col gap-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="text-base font-black text-gray-900 uppercase">{job.role}</h4>
                        <p className="text-sm font-bold text-gray-500">{job.company}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                        job.matchScore >= 90 ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {job.matchScore}% Match
                      </span>
                    </div>
                    {job.description && (
                      <p className="text-xs text-gray-600 leading-relaxed bg-white p-3 rounded-xl border border-gray-100">
                        "{job.description}"
                      </p>
                    )}
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleApply(job)}
                        className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-black text-xs uppercase hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                      >
                        Apply & Track <SafeIcon icon={FiExternalLink} />
                      </button>
                      <button
                        onClick={() => {
                          const newJobs = scannedJobs.filter(j => j !== job);
                          setScannedJobs(newJobs);
                          if (newJobs.length === 0) setShowScannedJobsModal(false);
                        }}
                        className="flex-none px-6 py-3 bg-white border border-gray-200 text-gray-500 rounded-xl font-black text-xs uppercase hover:bg-gray-50 hover:text-red-500 transition-all"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;